<script setup>
import {
  ref,
  watch,
  computed,
  onMounted,
  defineProps,
  defineEmits,
  defineModel,
  defineExpose,
} from "vue";
import { getCountries, parsePhoneNumberFromString } from "libphonenumber-js";
import countries from "world-countries";

import { ElInput, ElSelectV2 } from "element-plus";

const props = defineProps({
  only: {
    type: Array,
    default: null,
  },
  defaultCode: {
    type: String,
    default: "",
  },
  size: {
    type: String,
    default: "large",
  },
  placeholder: {
    type: String,
    default: "Enter phone number",
  },
  clearable: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  formattedValue: {
    type: Boolean,
    default: false,
  },
  friendlyValue: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["formatted", "focus"]);

const model = defineModel({
  default: "",
});

const phoneNumber = ref("");
const selectedCountryCode = ref();

const size = computed(() => props.size);
const defaultCode = computed(() => props.defaultCode);
const placeholder = computed(() => props.placeholder);
const clearable = computed(() => props.clearable);
const disabled = computed(() => props.disabled);
const formattedValue = computed(() => props.formattedValue);
const friendlyValue = computed(() => props.friendlyValue);
const countryCodes = computed(() => {
  if (!props.only) {
    const filteredCountries = getCountries().map((code) => {
      const flag = countries.find((c) => c.cca2 === code)?.flag;
      const isFallback = true;

      return {
        value: code,
        is_fallback: isFallback,
        url: `https://flagcdn.com/16x12/${code.toLowerCase()}.png`,
        label: isFallback ? `${code}` : `${code} - ${flag}`,
      };
    });

    return filteredCountries.map((country) => {
      if (country.value === "AC" || country.value === "TA") {
        return {
          value: country.value,
          label: country.value,
          is_fallback: true,
          url: "https://flagcdn.com/16x12/sh.png",
        };
      }

      return country;
    });
  }
  return props.only.map((code) => {
    const flag = countries.find((c) => c.cca2 === code)?.flag;
    const isFallback = true;

    return {
      value: code,
      is_fallback: isFallback,
      url: `https://flagcdn.com/16x12/${code.toLowerCase()}.png`,
      label: isFallback ? `${code}` : `${code} - ${flag}`,
    };
  });
});

const formatted = computed(() => {
  const phone = parsePhoneNumberFromString(
    phoneNumber.value,
    selectedCountryCode.value
  );

  return {
    countryCode: phone?.country || selectedCountryCode.value,
    phoneNumber: phone?.formatNational() || phoneNumber.value,
    formatted: phone?.formatInternational() || phoneNumber.value,
    countryCallingCode: phone?.countryCallingCode || "1",
    valid: phone?.isValid() ?? false,
    number: phone?.number || "",
  };
});

const handleInitialize = (value) => {
  if (!value) {
    return;
  }
  const phoneInitialized = parsePhoneNumberFromString(
    value,
    selectedCountryCode.value
  );

  if (value && phoneInitialized?.isValid()) {
    selectedCountryCode.value = phoneInitialized?.country;
    setTimeout(() => {
      phoneNumber.value = phoneInitialized?.number || value;
    }, 100);
  }
};

const handlePaste = () => {
  setTimeout(() => {
    handleInitialize(model.value);
  }, 20);
};

const handleFindFlagByCode = (code) => {
  const country = countries.find((c) => c.cca2 === code);
  if (country) {
    return `https://flagcdn.com/16x12/${country.cca2.toLowerCase()}.png`;
  }
  return "";
};

const handleFocus = (event) => {
  emit("focus", event);
};

watch(phoneNumber, (newValue) => {
  model.value = newValue;
});

watch(selectedCountryCode, () => {
  phoneNumber.value = "";
});

watch(
  formatted,
  (value) => {
    if (!value) {
      return;
    }
    if (value.valid) {
      if (formattedValue.value) {
        phoneNumber.value = value.formatted;
      } else if (friendlyValue.value) {
        phoneNumber.value = value.phoneNumber;
      } else {
        phoneNumber.value = value.number;
      }
    }

    if (!phoneNumber.value) {
      value.valid = true;
    }

    emit("formatted", value);
  },
  {
    deep: true,
    immediate: true,
    flush: "post",
  }
);

watch(model, () => {
  // handleInitialize(value);
});

onMounted(() => {
  if (defaultCode.value) {
    selectedCountryCode.value = defaultCode.value;
  }
  if (!defaultCode.value && countryCodes.value && countryCodes.value.length) {
    selectedCountryCode.value = countryCodes.value[0].value;
  }
  handleInitialize(model.value);
});

defineExpose({
  formatted,
});
</script>
<template>
  <div style="width: 100%">
    <ElInput
      v-model="phoneNumber"
      style="width: 100%"
      :disabled="!selectedCountryCode || disabled"
      class="input-with-select"
      :size="size"
      :placeholder="placeholder"
      :clearable="clearable"
      @focus="handleFocus"
      @paste="handlePaste"
    >
      <template #prepend>
        <ElSelectV2
          filterable
          :size="size"
          :options="countryCodes"
          v-model="selectedCountryCode"
          style="width: 110px"
          :teleported="true"
          :disabled="disabled"
        >
          <template #default="{ item }">
            <span v-if="!item.is_fallback">
              {{ item.label }}
            </span>
            <span
              v-if="item.is_fallback"
              class="flex gap-2 flex-row justify-start items-center"
            >
              {{ item.label }}
              <img :src="item.url" class="w-5" />
            </span>
          </template>
          <template #label="{ label, value }">
            <span class="flex gap-2 flex-row justify-start items-center">
              {{ label }}
              <img
                v-if="handleFindFlagByCode(value)"
                :src="handleFindFlagByCode(value)"
                class="w-5"
              />
            </span>
          </template>
        </ElSelectV2>
      </template>
    </ElInput>
  </div>
</template>

<style>
.input-with-select .el-input-group__prepend {
  background-color: var(--el-fill-color-blank);
}
</style>
